import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../../../context/AuthProvider'
import { api } from '../../../lib/api'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { Loader2, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react'

export default function RecurringDonationEdit() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [donation, setDonation] = useState(null)
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [amount, setAmount] = useState('')
  const [interval, setInterval] = useState('monthly')

  useEffect(() => {
    if (id && user?.id) {
      fetchDonation()
    }
  }, [id, user?.id])

  const fetchDonation = async () => {
    try {
      setLoading(true)
      setError(null)
      const donationData = await api.getRecurringDonationById(id)
      setDonation(donationData)
      setAmount((donationData.amountCents / 100).toFixed(2))
      setInterval(donationData.interval)
    } catch (err) {
      console.error('Failed to fetch recurring donation:', err)
      setError(err.message || 'Failed to load recurring donation')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!donation) return

    const amountNum = parseFloat(amount)
    if (isNaN(amountNum) || amountNum < 0.5) {
      setError('Please enter a valid amount (minimum $0.50)')
      return
    }

    // Check if anything changed
    const amountCents = Math.round(amountNum * 100)
    if (amountCents === donation.amountCents && interval === donation.interval) {
      setError('No changes detected')
      return
    }

    try {
      setSubmitting(true)
      setError(null)

      await api.updateRecurringDonation(id, {
        amountCents,
        interval,
      })

      // Navigate back to donations page with success message
      navigate('/app/donor/donations?recurring=updated')
    } catch (err) {
      console.error('Failed to update recurring donation:', err)
      setError(err.message || 'Failed to update recurring donation')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    )
  }

  if (error && !donation) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded p-6 mb-4">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
        <Button onClick={() => navigate('/app/donor/donations')}>
          Back to Donations
        </Button>
      </div>
    )
  }

  if (!donation) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded p-6 mb-4">
          <p className="text-red-600 text-sm">Recurring donation not found</p>
        </div>
        <Button onClick={() => navigate('/app/donor/donations')}>
          Back to Donations
        </Button>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <Button
          variant="outline"
          onClick={() => navigate('/app/donor/donations')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Donations
        </Button>
        <h1 className="text-3xl font-bold font-display text-charcoal mb-2">
          Edit Recurring Donation
        </h1>
        <p className="text-charcoal-muted">
          Update your recurring donation to {donation.parish?.name || 'your parish'}
        </p>
      </div>

      <div className="bg-white border border-surface-border rounded p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-red-600 font-semibold mb-1">Error</h3>
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-charcoal mb-2">
              Donation Amount ($)
            </label>
            <Input
              type="number"
              step="0.01"
              min="0.5"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value)
                setError(null)
              }}
              placeholder="0.00"
              required
              className="bg-surface-linen border-surface-border text-charcoal"
            />
            <p className="text-xs text-charcoal-muted mt-1">
              Minimum donation: $0.50
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-charcoal mb-2">
              Donation Frequency
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setInterval('weekly')}
                className={`px-4 py-3 rounded border-2 transition-colors ${
                  interval === 'weekly'
                    ? 'border-gold bg-gold/10 text-charcoal'
                    : 'border-surface-border bg-surface-linen text-charcoal hover:border-gold/50'
                }`}
              >
                <div className="font-semibold">Weekly</div>
                <div className="text-xs text-charcoal-muted mt-1">
                  Every week
                </div>
              </button>
              <button
                type="button"
                onClick={() => setInterval('monthly')}
                className={`px-4 py-3 rounded border-2 transition-colors ${
                  interval === 'monthly'
                    ? 'border-gold bg-gold/10 text-charcoal'
                    : 'border-surface-border bg-surface-linen text-charcoal hover:border-gold/50'
                }`}
              >
                <div className="font-semibold">Monthly</div>
                <div className="text-xs text-charcoal-muted mt-1">
                  Every month
                </div>
              </button>
            </div>
          </div>

          <div className="bg-gold/10 border border-gold/30 rounded p-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-charcoal mt-0.5 flex-shrink-0" />
              <div className="text-sm text-charcoal/90">
                <p className="font-semibold mb-1">Updated Recurring Donation Summary</p>
                <p className="text-charcoal-muted">
                  You will be charged <strong>${amount || '0.00'}</strong> {interval === 'weekly' ? 'every week' : 'every month'} to{' '}
                  <strong>{donation.parish?.name || 'your parish'}</strong>. Changes will take effect on your next billing cycle.
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/app/donor/donations')}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || !amount}
              className="flex-1"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Recurring Donation'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
